import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography
} from "@material-ui/core";
import { WhatsApp } from "@material-ui/icons";
import { Facebook } from "lucide-react";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const loadFacebookSdk = (appId) => {
  if (!appId) return Promise.reject(new Error("Facebook App ID não configurado."));

  return new Promise((resolve, reject) => {
    if (window.FB) {
      window.FB.init({
        appId,
        cookie: true,
        xfbml: false,
        version: "v21.0"
      });
      resolve(window.FB);
      return;
    }

    window.fbAsyncInit = function fbAsyncInit() {
      window.FB.init({
        appId,
        cookie: true,
        xfbml: false,
        version: "v21.0"
      });
      resolve(window.FB);
    };

    if (document.getElementById("facebook-jssdk")) return;

    const script = document.createElement("script");
    script.id = "facebook-jssdk";
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.src = "https://connect.facebook.net/pt_BR/sdk.js";
    script.onerror = () => reject(new Error("Não foi possível carregar o SDK da Meta."));
    document.body.appendChild(script);
  });
};

const initialManualValues = {
  name: "",
  phoneNumberId: "",
  wabaId: "",
  wabaBusinessId: ""
};

const WabaModal = ({ open, onClose }) => {
  const startedRef = useRef(false);
  const [credentials, setCredentials] = useState({
    name: "",
    facebookAppId: "",
    facebookConfigId: "",
    whatsappBusinessToken: ""
  });
  const [manualValues, setManualValues] = useState(initialManualValues);
  const [loading, setLoading] = useState(false);
  const [manualLoading, setManualLoading] = useState(false);
  const [dialogMode, setDialogMode] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const loadCredentials = useCallback(async () => {
    const { data } = await api.get("/integrations/meta/credentials");
    const waba = data?.waba || {};
    return {
      name: "",
      facebookAppId: waba.facebookAppId || "",
      facebookConfigId: waba.facebookConfigId || "",
      whatsappBusinessToken: waba.whatsappBusinessToken || ""
    };
  }, []);

  const handleClose = () => {
    startedRef.current = false;
    setCredentials({
      name: "",
      facebookAppId: "",
      facebookConfigId: "",
      whatsappBusinessToken: ""
    });
    setManualValues(initialManualValues);
    setDialogMode(null);
    setErrorMessage("");
    setLoading(false);
    setManualLoading(false);
    onClose();
  };

  const handleManualChange = (event) => {
    const { name, value } = event.target;
    setManualValues((current) => ({ ...current, [name]: value }));
  };

  const openErrorDialog = (message) => {
    setErrorMessage(message || "Login cancelado ou não autorizado pelo Facebook.");
    setDialogMode("error");
    setLoading(false);
  };

  const connectEmbedded = useCallback(async (loadedCredentials) => {
    const values = loadedCredentials || credentials;
    setLoading(true);
    setDialogMode(null);
    setErrorMessage("");

    try {
      const configId = values.facebookConfigId.trim();
      const facebookAppId = values.facebookAppId.trim();

      if (!facebookAppId) {
        throw new Error("WhatsApp App ID não configurado em Integrações > Meta.");
      }
      if (!configId) {
        throw new Error("Facebook Login Configuration ID não configurado.");
      }

      const FB = await loadFacebookSdk(facebookAppId);

      FB.login(
        async (response) => {
          const code = response?.authResponse?.code || response?.code;
          if (!code) {
            openErrorDialog(
              response?.status !== "connected"
                ? "Login cancelado ou não autorizado pelo Facebook."
                : "A Meta não retornou o código de autorização."
            );
            return;
          }

          try {
            await api.post("/waba/embedded-signup", {
              code,
              name: values.name
            });
            toast.success("WhatsApp API Oficial conectado com cadastro incorporado.");
            handleClose();
          } catch (err) {
            openErrorDialog(
              err?.response?.data?.error ||
              err?.response?.data?.message ||
              err?.message ||
              "Erro no cadastro incorporado."
            );
          } finally {
            setLoading(false);
          }
        },
        {
          config_id: configId,
          response_type: "code",
          override_default_response_type: true,
          extras: {
            setup: {},
            featureType: "",
            sessionInfoVersion: "3"
          }
        }
      );
    } catch (err) {
      openErrorDialog(err.message);
    }
  }, [credentials]);

  useEffect(() => {
    if (!open || startedRef.current) return;

    startedRef.current = true;
    setLoading(true);
    loadCredentials()
      .then((loadedCredentials) => {
        setCredentials(loadedCredentials);
        connectEmbedded(loadedCredentials);
      })
      .catch((err) => {
        openErrorDialog(
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Não foi possível carregar as credenciais da Meta."
        );
      });
  }, [connectEmbedded, loadCredentials, open]);

  const connectManual = async () => {
    setManualLoading(true);
    try {
      await api.post("/waba/", {
        ...manualValues,
        wabaToken: credentials.whatsappBusinessToken
      });
      toast.success(i18n.t("connections.wabaModal.success") || "WhatsApp API Oficial conectado com sucesso!");
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setManualLoading(false);
    }
  };

  if (!open || (!dialogMode && loading)) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gridGap={10}>
          <WhatsApp style={{ color: "#128C7E", fontSize: 20 }} />
          <span>{i18n.t("connections.wabaModal.title") || "WhatsApp Business API (Oficial)"}</span>
          <Box
            component="span"
            style={{
              background: "#0f9f8f",
              color: "#fff",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              padding: "3px 8px"
            }}
          >
            API Oficial
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {dialogMode === "error" && (
          <Box textAlign="center" py={3}>
            <Typography variant="body1" color="textSecondary" style={{ marginBottom: 16 }}>
              {errorMessage}
            </Typography>
            <Button
              color="primary"
              variant="contained"
              onClick={() => connectEmbedded()}
              disabled={loading}
              startIcon={<Facebook size={16} />}
              style={{ textTransform: "none", marginBottom: 12 }}
            >
              {loading ? <CircularProgress size={18} /> : "Tentar novamente"}
            </Button>
            <Box>
              <Button
                color="default"
                onClick={() => setDialogMode("manual")}
                style={{ textTransform: "none", textDecoration: "underline" }}
              >
                Configurar manualmente
              </Button>
            </Box>
          </Box>
        )}

        {dialogMode === "manual" && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label={i18n.t("connections.wabaModal.name") || "Nome da conexão"}
                value={manualValues.name}
                onChange={handleManualChange}
                fullWidth
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="phoneNumberId"
                label="Phone Number ID *"
                value={manualValues.phoneNumberId}
                onChange={handleManualChange}
                fullWidth
                required
                variant="outlined"
                helperText="Encontrado em WhatsApp > API Setup no painel da Meta"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="wabaId"
                label="WhatsApp Business Account ID"
                value={manualValues.wabaId}
                onChange={handleManualChange}
                fullWidth
                required
                variant="outlined"
                helperText="Necessário para listar templates"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="wabaBusinessId"
                label="Business ID (opcional)"
                value={manualValues.wabaBusinessId}
                onChange={handleManualChange}
                fullWidth
                variant="outlined"
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        {dialogMode === "manual" && (
          <Button
            color="default"
            onClick={() => {
              setDialogMode(null);
              connectEmbedded();
            }}
            disabled={manualLoading}
            style={{ textTransform: "none", textDecoration: "underline", marginRight: "auto" }}
          >
            Voltar ao cadastro incorporado
          </Button>
        )}
        <Button onClick={handleClose} disabled={loading || manualLoading}>
          {i18n.t("connections.wabaModal.cancel") || "Cancelar"}
        </Button>
        {dialogMode === "manual" && (
          <Button
            onClick={connectManual}
            color="primary"
            variant="contained"
            disabled={manualLoading || !manualValues.phoneNumberId || !manualValues.wabaId}
            style={{ textTransform: "none" }}
          >
            {manualLoading ? <CircularProgress size={20} /> : "Conectar"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default WabaModal;
